/**
 * The guided tour: seven cards, each one pointing at a real control.
 *
 * WHY IT POINTS AT THE PRODUCT AND NOT AT A PICTURE OF IT. A tour built from
 * screenshots is wrong the first time anything moves, and the reader has to
 * translate from the picture to the screen anyway. This one reads a selector out
 * of `TOUR`, measures the element that is actually on screen, and cuts a hole in
 * the dimmer over it. If the element is not there — the rail is a drawer below
 * 900px, a stop names something a route does not have — the card centres itself
 * and says its piece without a spotlight. A missing anchor must never be a dead
 * end in a walkthrough for someone who has never seen the app.
 *
 * The hole is one `box-shadow: 0 0 0 9999px` on a positioned box, not an SVG
 * mask and not four divs: one element to measure, one to move, and the corner
 * radius comes for free.
 *
 * IT BLOCKS THE PAGE, DELIBERATELY. The tour shows; it does not ask anyone to
 * do. Letting clicks through would mean a reader three sentences into "this is
 * the search box" can navigate away mid-sentence and land somewhere the next
 * stop does not describe. Escape, the × and Done all end it, and the whole thing
 * is skippable from the first card.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';

import { TOUR } from '@/features/onboarding/content';
import { useOnboardingState } from '@/features/onboarding/useOnboarding';
import { Button, IconButton } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useHotkeyScope } from '@/lib/keyboard';
import { useMediaQuery } from '@/lib/viewport';

/* ────────────────────────────────────────────────────────────────────────────
   Imperative open — the same pattern as the ? sheet and the palette
   ──────────────────────────────────────────────────────────────────────────── */

export const TOUR_EVENT = 'af:tour';

/** Start the tour from anywhere: a menu item, a command, a button on a card. */
export function startTour(): void {
  window.dispatchEvent(new CustomEvent(TOUR_EVENT));
}

/* ────────────────────────────────────────────────────────────────────────────
   Geometry
   ──────────────────────────────────────────────────────────────────────────── */

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Breathing room around the highlighted control, so the ring is not a tracing. */
const PAD = 6;
const CARD_W = 340;
const CARD_GAP = 12;
/**
 * Below this the card is a bottom sheet rather than a floating panel: 340px of
 * card beside a highlighted control does not fit on a 390px screen, and the
 * shell's own answer at that width — the nav drawer, the promoted record pane —
 * is a surface that takes the whole edge rather than one that floats.
 */
const NARROW = 560;
const NARROW_QUERY = `(max-width: ${NARROW - 0.02}px)`;

function measure(selector: string | null): Rect | null {
  if (!selector) return null;
  const element = document.querySelector<HTMLElement>(selector);
  if (!element) return null;
  const box = element.getBoundingClientRect();
  if (box.width === 0 || box.height === 0) return null;
  return {
    top: box.top - PAD,
    left: box.left - PAD,
    width: box.width + PAD * 2,
    height: box.height + PAD * 2,
  };
}

/**
 * Put the card next to the hole and keep it on screen.
 *
 * Preference order is right, then below, then left, then above — the reading
 * order of the shell's own chrome, so the card lands where the eye already is.
 * Whatever survives is clamped into the viewport with an 8px margin, because a
 * card half off the bottom edge on a laptop is the failure mode every tour has.
 */
function placeCard(rect: Rect | null, cardHeight: number): { top: number; left: number } | null {
  if (typeof window === 'undefined') return null;
  if (!rect) return null; // centred — see the render
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const margin = 8;
  let left = rect.left + rect.width + CARD_GAP;
  let top = rect.top;

  if (left + CARD_W > vw - margin) {
    // No room on the right: try under it, then to the left of it.
    if (rect.top + rect.height + CARD_GAP + cardHeight < vh - margin) {
      left = rect.left;
      top = rect.top + rect.height + CARD_GAP;
    } else {
      left = rect.left - CARD_W - CARD_GAP;
      top = rect.top;
    }
  }

  return {
    left: Math.min(Math.max(margin, left), Math.max(margin, vw - CARD_W - margin)),
    top: Math.min(Math.max(margin, top), Math.max(margin, vh - cardHeight - margin)),
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   The overlay
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Mounted once by the app shell, alongside ⌘K and the `?` sheet. It renders
 * nothing at all until somebody starts it — no dimmer, no listener cost, and no
 * first-run modal in anybody's face. The tour is offered on the Getting started
 * screen and in the account menu; it never opens itself.
 */
export function TourOverlay() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [cardHeight, setCardHeight] = useState(220);
  const cardRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useOnboardingState();
  const narrow = useMediaQuery(NARROW_QUERY);

  const stop = TOUR[index] ?? TOUR[0]!;
  const last = index === TOUR.length - 1;

  useHotkeyScope('tour', open);

  useEffect(() => {
    const onStart = () => {
      setIndex(0);
      setOpen(true);
    };
    window.addEventListener(TOUR_EVENT, onStart);
    return () => window.removeEventListener(TOUR_EVENT, onStart);
  }, []);

  /* Re-measure on the stop, on a resize and on a scroll. The spotlight is drawn
     in viewport coordinates, so anything that moves the page moves the hole. */
  useLayoutEffect(() => {
    if (!open) return;
    const sync = () => setRect(measure(stop.anchor));
    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
    };
  }, [open, stop.anchor]);

  useLayoutEffect(() => {
    if (!open) return;
    const height = cardRef.current?.offsetHeight;
    if (height && height !== cardHeight) setCardHeight(height);
  }, [open, index, cardHeight]);

  const finish = useCallback(
    (completed: boolean) => {
      setOpen(false);
      setIndex(0);
      if (completed && !state.tourSeen) setState({ ...state, tourSeen: true });
    },
    [setState, state],
  );

  const next = useCallback(() => {
    if (last) finish(true);
    else setIndex((value) => value + 1);
  }, [finish, last]);

  const back = useCallback(() => setIndex((value) => Math.max(0, value - 1)), []);

  /*
    The keys a walkthrough owes: Escape leaves, the arrows step, and Tab stays
    inside the card.

    The trap is hand-rolled rather than borrowed from Radix's Dialog because
    this overlay is not one: a Radix dialog renders in a portal with the page
    behind it `aria-hidden`, and its own focus guards would fight the spotlight
    for the element being pointed AT. What the reader needs is the same
    guarantee, though — Tab off the last button on a covered page hands focus to
    a control nobody can see or click.
  */
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        finish(false);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        next();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        back();
        return;
      }
      if (event.key !== 'Tab') return;

      const card = cardRef.current;
      if (!card) return;
      const stops = [...card.querySelectorAll<HTMLElement>('button:not([disabled])')];
      if (stops.length === 0) return;
      const first = stops[0]!;
      const last = stops[stops.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === card)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (active instanceof Node && !card.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, finish, next, back]);

  /* Focus lands on the card when the stop changes, so a screen reader reads the
     new title instead of leaving the reader on a button that has moved. */
  useEffect(() => {
    if (!open) return;
    cardRef.current?.focus();
  }, [open, index]);

  if (!open) return null;

  const placed = narrow ? null : placeCard(rect, cardHeight);

  return (
    <div className="fixed inset-0 z-[var(--z-modal)]" data-tour-overlay="">
      {/*
        The dimmer and the hole are the same element. With an anchor it is a
        positioned box carrying a 9999px shadow — everything outside the box is
        covered, the box itself is not. With no anchor it is the whole viewport,
        which is the plain dimmer a centred card wants.
      */}
      {rect ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute rounded-[var(--radius-md)] transition-[top,left,width,height] duration-[var(--dur-normal)] ease-[var(--ease-out)]"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            boxShadow: '0 0 0 9999px var(--scrim), 0 0 0 2px var(--focus) inset',
          }}
        />
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-[var(--scrim)]" />
      )}

      {/* Swallows every click that is not on the card. See the header. */}
      <div className="absolute inset-0" onClick={() => finish(false)} />

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="af-tour-title"
        aria-describedby="af-tour-body"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'absolute flex flex-col gap-3 border bg-[var(--surface-overlay)] p-4',
          'border-line shadow-[var(--shadow-4)] outline-none',
          narrow
            ? 'inset-x-0 bottom-0 rounded-t-[var(--radius-lg)]'
            : 'rounded-[var(--radius-lg)]',
        )}
        style={
          narrow
            ? undefined
            : placed
              ? { top: placed.top, left: placed.left, width: CARD_W }
              : {
                  top: '50%',
                  left: '50%',
                  width: CARD_W,
                  transform: 'translate(-50%, -50%)',
                }
        }
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="eyebrow">
              Step {index + 1} of {TOUR.length}
            </p>
            <h2
              id="af-tour-title"
              className="mt-1 text-lg leading-tight font-semibold tracking-[var(--tracking-tight)] text-primary"
            >
              {stop.title}
            </h2>
          </div>
          <IconButton size="sm" variant="ghost" label="Close the tour" onClick={() => finish(false)}>
            <X />
          </IconButton>
        </div>

        <p id="af-tour-body" className="text-base leading-relaxed text-secondary">
          {stop.body}
        </p>

        {/* Where you are, as dots. Seven of them fit; a progress bar for seven
            steps is a bar that moves in visible jumps and says less. */}
        <div aria-hidden="true" className="flex items-center gap-1.5">
          {TOUR.map((entry, position) => (
            <span
              key={entry.id}
              className={cn(
                'h-1 rounded-[var(--radius-full)] transition-[width,background-color] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
                position === index
                  ? 'w-4 bg-[var(--text-secondary)]'
                  : position < index
                    ? 'w-1 bg-[var(--text-muted)]'
                    : 'w-1 bg-[var(--line-strong)]',
              )}
            />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => finish(false)}>
            {last ? 'Close' : 'Skip'}
          </Button>
          <span className="flex-1" />
          {index > 0 ? (
            <Button size="sm" variant="secondary" iconLeft={<ArrowLeft />} onClick={back}>
              Back
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="secondary"
            iconRight={last ? undefined : <ArrowRight />}
            onClick={next}
          >
            {last ? 'Done' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}
